/* eslint-disable no-irregular-whitespace */
/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { Box, Typography, Container, Stack } from '@mui/material';

const TermsOfService = () => (
  <Box
    component="main"
    sx={{ flexGrow: 1, position: 'relative', zIndex: 10, pt: { xs: 4, md: 6 }, pb: 6 }}
  >
    <Container maxWidth="md" sx={{ px: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box mb={4} textAlign="left">
        <Typography
          variant="overline"
          color="primary.main"
          fontWeight={800}
          letterSpacing={1.5}
          display="block"
          mb={1}
        >
          LEGAL
        </Typography>
        <Typography
          variant="h3"
          component="h1"
          color="text.primary"
          mb={1.5}
          sx={{
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            letterSpacing: '-0.5px',
            fontWeight: 700,
          }}
        >
          Master Services Agreement
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last updated: 18-Apr-2026
        </Typography>
      </Box>

      {/* Content Section */}
      <Box>
        {/* Preamble Box */}
        <Box
          mb={4}
          p={3}
          sx={{ bgcolor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 2 }}
          >
            THIS MASTER SERVICES AGREEMENT GOVERNS ACCESS AND USE OF THE SERVICES PROVIDED BY
            MERUDESK. THIS AGREEMENT IS ENTERED INTO BY THE CUSTOMER ENTITY ENTERING INTO A SERVICE
            ORDER FOR OR REGISTERING TO USE MERUDESK SERVICES AND CYBERMERU TECHNOLOGIES PRIVATE
            LIMITED ENTERING INTO A SERVICE ORDER WITH CUSTOMER (“MERUDESK”).
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 2 }}
          >
            BY ACCEPTING THIS AGREEMENT, EITHER BY EXECUTING A SERVICE ORDER THAT REFERENCES THIS
            AGREEMENT OR BY CLICKING A BOX INDICATING ACCEPTANCE, THE INDIVIDUAL DOING SO AGREES TO
            THE TERMS OF THIS AGREEMENT. THIS AGREEMENT APPLIES AS OF THE "CURRENT AS OF" DATE ABOVE
            AND IS EFFECTIVE AS OF THE DATE THE CUSTOMER ACCEPTS THE AGREEMENT.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            IF AN INDIVIDUAL ACCEPTS THIS AGREEMENT ON BEHALF OF AN ENTITY, THAT INDIVIDUAL
            REPRESENTS THAT THEY HAVE THE AUTHORITY TO BIND THE ENTITY TO THE TERMS OF THIS
            AGREEMENT, IN WHICH CASE “CUSTOMER” WILL REFER TO THE ENTITY. IF AN INDIVIDUAL DOES NOT
            HAVE SUCH AUTHORITY OR DOES NOT AGREE TO THE TERMS OF THIS AGREEMENT, THEY MUST NOT
            ACCEPT THIS AGREEMENT AND THEY AND CUSTOMER MAY NOT USE THE SERVICES.
          </Typography>
        </Box>

        <Stack spacing={3}>
          {/* Section 1 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              1. Definitions
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Affiliate”</strong> means an entity that, directly or indirectly, controls,
              is controlled by, or is under common control with the subject entity, with “control”
              meaning the direct or indirect control of more than fifty percent (50%) of the voting
              power of the subject entity or the power to direct the management of that entity.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Agreement”</strong> means this Master Services Agreement, together with any
              Service Orders entered into between Customer and MeruDesk and any documents or
              materials of any nature incorporated by reference into the foregoing.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Candidate”</strong> means an individual registering themselves for self-usage
              of the services or invited by Customer to use the Services as the subject of an
              evaluation and/or assessment by Customer in connection with Customer’s use of the
              Services. A Candidate is not considered a “User” under this Agreement.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Customer”</strong> means the Individual, company or other legal entity
              ordering or registering to use the Services pursuant to a Service Order, Free Trial
              use, or other agreement with MeruDesk.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Customer Data”</strong> means all data, information, and/or materials input,
              uploaded, or submitted to the Services by Customer or its Users or Candidates in
              connection with Customer’s use of the Services. Customer Data does not include any
              Usage Data (defined below).
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Documentation”</strong> means usage guides, technical documentation, and
              specifications for the Services that MeruDesk makes available to Customer in
              connection with or through the Services.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Services”</strong> means the products and/or services offered by MeruDesk
              that are ordered by Customer.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Service Order”</strong> means an online order or an ordering document entered
              into by Customer and MeruDesk specifying the Services purchased by Customer and to be
              provided by MeruDesk.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Services Term”</strong> means the duration of Customer’s subscription to the
              Services as provided for by the applicable Service Order.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“Usage Data”</strong> means data derived from use or operation of the Services
              that exists in an aggregated and anonymized and/or de-identified format such that
              Customer and individual Users and Candidates are not identifiable.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>“User”</strong> means Customer’s employees or contractors who are designated
              and granted access by Customer to use the Services under Customer's account as
              permitted by this Agreement.
            </Typography>
          </Box>

          {/* Section 2 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              2. Term and Service Orders
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>2.1. Term.</strong> This Agreement is effective as of the date Customer
              accepts the Agreement and will continue until the Services Terms of all Service Orders
              have expired or are terminated in accordance with this Agreement (the “Term”).
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>2.2. Service Orders.</strong> Customer will order the Services by executing a
              Service Order. Each Service Order will include terms detailing the applicable
              Services. Service Orders and the obligations under them are non-cancellable, except as
              may be provided for by this Agreement.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>2.3. Adding Services.</strong> Customer may purchase additional Services
              during the Term by entering into an additional Service Order as MeruDesk requires to
              add those Services. For additional subscription-based Services added: the Services
              Term will continue coterminous with Customer’s then-current Services Term; and Fees
              will be prorated commensurate with the remaining period of Customer’s then-current
              Services Term as of the start date of the additional Services.
            </Typography>
          </Box>

          {/* Section 3 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              3. Provision of Services
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>3.1. Access and Use.</strong> MeruDesk will provide Customer and its Users
              access to and use of the Services and Documentation in accordance with the applicable
              Service Order. Customer and its Users will use the Services solely for Customer’s
              internal business purposes.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>3.2. Hosting, Support, Service Levels.</strong> MeruDesk will host, maintain,
              and support the Services in accordance with the service levels set forth in MeruDesk’s
              Service Level Agreement.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>3.3. AI Features.</strong> If Customer makes use of any features or
              functionality within the Services that MeruDesk discloses to Customer as making use of
              artificial intelligence, machine learning, or similar technologies (“AI Features”),
              Customer’s use of those AI Features are subject to the AI Feature Terms.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>3.4. Personal Data Processing.</strong> With respect to MeruDesk’s processing
              of any personal data or personal information (as defined therein), MeruDesk and
              Customer agree to the terms of MeruDesk’s Data Processing Addendum.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>3.5. Feature Updates.</strong> MeruDesk may periodically update and/or modify
              the features and functionality of the Services, provided that any update or
              modification will not materially diminish the features or functionality of the
              Services provided to Customer during the applicable Services Term.
            </Typography>
          </Box>

          {/* Section 4 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              4. Fees; Payment; Taxes
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>4.1. Fees.</strong> Customer will pay MeruDesk the fees for the Services as
              specified in the applicable Service Order (“Fees”). All Fees are non-refundable except
              as provided for by this Agreement and are not subject to apportionment by Customer.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>4.2. Invoicing and Payment.</strong> Fees for each Services Term are due
              upfront as stated in the Service Order. Fees are payable in U.S. dollars, unless
              Customer is registered under the laws of India in which case Fees are payable in INR.
              Overdue Fees will bear interest at the lesser rate of 1.5% per month or the highest
              rate permitted by applicable law, calculated daily and compounded monthly.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>4.3. Taxes.</strong> Unless otherwise stated in the Service Order or if
              Customer provides MeruDesk with a valid tax exempt certificate, all Fees are exclusive
              of taxes, levies, duties, or similar governmental assessments or charges that may be
              imposed by applicable authorities in connection with Customer’s purchase of the
              Services, all of which are the sole responsibility of Customer and will be charged to
              Customer if MeruDesk is required by applicable law or regulation to collect or pay
              them. Customer’s foregoing payment responsibility excludes any taxes, levies, duties,
              or similar governmental assessments based on MeruDesk’s revenue, income, number of
              employees, or corporate existence.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>4.4. Trial Offers.</strong> If Customer registers for a free trial use of the
              Services to the extent offered by MeruDesk (a “Free Trial”), MeruDesk will make the
              applicable portion of Services available to Customer free of charge until the earlier
              of: (a) the end of the Free Trial period offered by MeruDesk; or (b) the start date of
              any Service Order entered into between Customer and MeruDesk for Customer’s paid use
              of the Services. Free Trials are subject to this Agreement and to any additional Free
              Trial terms presented in connection with the Free Trial registration, all of which are
              incorporated into this Agreement by reference. ANY CUSTOMER DATA PROCESSED BY MERUDESK
              DURING A FREE TRIAL USE WILL BE PERMANENTLY LOST UNLESS CUSTOMER PURCHASES A
              SUBSCRIPTION TO THE SERVICES BEFORE THE END OF THE FREE TRIAL PERIOD.
            </Typography>
          </Box>

          {/* Section 5 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              5. Usage Requirements
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>5.1. User Types.</strong> Users may be designated different usage rights and
              limitations based on their User type as specified in the applicable Service Order.
              Customer may use the Services only in accordance with the User numbers, rights, and
              limitations permitted by the Agreement. User accounts: (a) must be designated to one
              unique individual and may not be shared or accessed by multiple individuals or of a
              generic nature; (b) may be re-assigned by Customer during a Services Term only on a
              permanent basis to a new User replacing one who will no longer use the Services; and
              (c) may only be used by the Customer entity entering into the applicable Services
              Order, unless this Agreement provides otherwise.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>5.2. Acceptable Use Policy.</strong> In accordance with the following MeruDesk
              “Acceptable Use Policy,” Customer and its Users may not, or allow any third party to,
              do any of the following in connection with use of the Services: (a) use the Services
              in violation of applicable law; (b) take, or attempt to take, any action to damage,
              disable, or interfere with the operation or security of the Services or circumvent any
              security, access controls, or use limits of the Service; (c) initiate a
              denial-of-service attack, software virus, or other harmful computer code, file, or
              program (including Trojan horses, worms, time bombs, cancelbots, malware, or spyware),
              unsolicited communication, spam, false source-identifying information (including
              “phishing” or “spoofing”); (d) use the Services in any manner, or post, upload, or
              transmit any material to the Services, that MeruDesk deems libelous, discriminatory,
              abusive, harassing, obscene, fraudulent, offensive, or objectionable; (e) infringe
              upon or violate the rights of MeruDesk or any third parties, including any copyright,
              trademark, trade secret, patent, or other intellectual property rights (whether or not
              registered or issued) (collectively, “Intellectual Property Rights”) or remove or
              alter any notices, attribution, or labels of Intellectual Property Rights published in
              connection with the Services or Documentation; (f) copy, distribute, resell, rent or
              lease, mirror, frame, publicly display, modify, compile or decompile, disassemble,
              reverse engineer, or create derivative works of any part of the Services; or (g) use
              the Services for competitive analysis or for the development or provision of a
              compeding service or product.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>5.3. Compliance Monitoring.</strong> Customer is solely responsible for
              ensuring its Users and its Customer Data comply with the terms of this Agreement. Any
              action taken by a User with respect to the Services will be deemed an action taken by
              Customer. MeruDesk reserves the right (but will not have the obligation) to monitor
              use of the Services for compliance with this Agreement.
            </Typography>
          </Box>

          {/* Section 6 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              6. Intellectual Property Rights
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>6.1. Ownership.</strong> As between MeruDesk and Customer, except for the
              rights granted by this Agreement: (a) Customer owns and retains all its rights, title,
              and interest in and to all Customer Data and all Intellectual Property Rights therein;
              and (b) MeruDesk, its Affiliates, and its licensors, respectively, own and retain all
              rights, title, and interest in and to all Usage Data and the Services and all
              Intellectual Property Rights therein.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>6.2. Customer Data License.</strong> Customer grants MeruDesk and its
              Affiliates a worldwide, non-exclusive, royalty-free, sublicensable (solely to
              MeruDesk’s third party providers or licensors as necessary to provide the Services to
              Customer) license solely to use, host, process, copy, transmit, and display Customer
              Data in connection with MeruDesk’s performance of its obligations under this
              Agreement.
            </Typography>
          </Box>

          {/* Section 7 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              7. Confidentiality
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>7.1. Confidential Information.</strong> “Confidential Information” means any
              information disclosed by a party (“Disclosing Party”) to the other party (“Receiving
              Party”), whether orally or in writing, that is identified as confidential or should
              reasonably be understood to be confidential due to the nature of the information and
              the circumstances of the disclosure, including Customer Data, and any non-public
              information related to the Services or Documentation, software, know-how, processes,
              trade secrets, business and marketing plans, technology and technical information,
              product plans and designs, business and operational methods, and accounting and
              financial records. Confidential Information does not include any information that is:
              (a) known to the Receiving Party or within its possession prior to disclosure by the
              Disclosing Party; (b) made available to the public through no act or omission of the
              Receiving Party; (c) disclosed to the Receiving Party on a non-confidential basis by a
              third party having the right to make that disclosure; or (d) independently developed
              by the Receiving Party without reference to or reliance upon the Disclosing Party’s
              Confidential Information.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>7.2. Confidentiality Obligations.</strong> The Receiving Party will keep the
              Disclosing Party’s Confidential Information confidential, exercising the same degree
              of care to do so that the Receiving Party exercises to keep confidential its own
              Confidential information of similar type and importance, but no less than reasonable
              care. The Receiving Party will not use or disclose to a third party any Confidential
              Information of the Disclosing Party, except: (a) with the Disclosing Party’s prior
              written consent; (b) to the employees, directors, officers, attorneys, accountants,
              agents, or contractors of the Receiving Party or its Affiliates who need to know the
              Confidential Information for purposes consistent with this Agreement and who have
              agreed to keep the Confidential Information confidential under terms no less
              protective thereof than the terms of this Agreement; or (c) as required by law or by
              order of a court or adjudicator of competent jurisdiction, in which case the Receiving
              Party will, prior to any disclosure, promptly provide the Disclosing Party with
              written notice of the required disclosure such that the Disclosing Party will have the
              opportunity to contest or limit the scope of the disclosure and, if disclosure is
              nonetheless required, will reasonably cooperate (at the Disclosing Party’s expense)
              with the Disclosing Party’s reasonable and lawful requests related thereto.
            </Typography>
          </Box>

          {/* Section 8 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              8. Representations and Warranties
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>8.1. Mutual.</strong> MeruDesk and Customer each represent and warrant that it
              has full right and authority to enter into this Agreement and to perform all its
              obligations and grant all rights granted by it under this Agreement.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>8.2. By MeruDesk.</strong> MeruDesk represents and warrants that the Services
              will materially conform to the specifications of the applicable Service Order and
              Documentation during the applicable Services Term.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>8.3. By Customer.</strong> Customer represents and warrants that it has
              secured all rights in Customer Data as may be necessary for MeruDesk to process the
              Customer Data in accordance with this Agreement.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>8.4. Disclaimer.</strong> EXCEPT AS PROVIDED FOR BY THIS AGREEMENT, THE
              SERVICES ARE PROVIDED “AS-IS” AND AS AVAILABLE AND MERUDESK DISCLAIMS AND DOES NOT
              MAKE ANY REPRESENTATION OR WARRANTY RELATED TO THE SERVICES, WHETHER EXPRESS OR
              IMPLIED, STATUTORY OR OTHERWISE, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, NONINFRINGEMENT, TITLE, OR THAT THE SERVICES WILL BE
              UNINTERRUPTED, COMPLETELY SECURE, ERROR-FREE, OR MEET ANY PARTICULAR REQUIREMENT OF
              CUSTOMER.
            </Typography>
          </Box>

          {/* Section 9 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              9. Indemnification
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>9.1. Indemnification by MeruDesk.</strong> MeruDesk will indemnify, defend,
              and hold harmless Customer and its Affiliates from and against any losses, damages,
              liabilities, suits, actions, costs, and expenses, including reasonable attorneys’ fees
              (together, “Losses”), arising out of or related to any third party claim alleging that
              the Services infringe on that third party’s Intellectual Property Rights. In the event
              of a claim giving rise to MeruDesk’s indemnification obligation under this Agreement,
              MeruDesk may, at its election and sole expense: (a) procure the right for Customer to
              continue using the applicable Services; (b) replace or modify the applicable Services
              so that they are no longer infringing; or (c) terminate the applicable Services on
              written notice to Customer and refund to Customer the pro-rata amount of any pre-paid
              Fees for the portion of the applicable Services remaining prospectively unused as a
              result of the termination. MeruDesk’s obligation to indemnify Customer will not apply
              to Customer’s modification to Services or to Customer’s combination of the Services
              with third party products, services, or materials, which are not permitted by this
              Agreement or otherwise made or approved by MeruDesk (“Unauthorized Changes”) or to a
              claim to the extent arising from or related to Customer’s violation of this Agreement.
              The indemnification obligations of this Section constitute MeruDesk’s entire liability
              and Customer’s exclusive remedy with respect to any actual or alleged third party
              claim of infringement or misappropriation of Intellectual Property Rights.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>9.2. Indemnification by Customer.</strong> Customer will indemnify, defend,
              and hold harmless MeruDesk and its Affiliates from and against any Losses arising out
              of or related to any third party claim to the extent arising from or related to: (a)
              Customer Data; or (b) Customer’s Unauthorized Changes to the Services.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>9.3. Indemnification Process.</strong> With respect to a claim subject to
              indemnification, the party seeking indemnification will provide the other party with
              prompt written notice of the claim, control over the defense and settlement of the
              claim, and information and assistance as may be reasonably requested by the
              indemnifying party to assist the indemnifying party in settling and/or defending the
              claim.
            </Typography>
          </Box>

          {/* Section 10 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              10. Limitation of Liability
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>10.1. Limitation of Certain Damages.</strong> TO THE FULLEST EXTENT PERMITTED
              BY APPLICABLE LAW, NEITHER PARTY NOR ITS AFFILIATES WILL BE LIABLE TO THE OTHER PARTY
              OR ITS AFFILIATES FOR ANY INCIDENTAL, SPECIAL, INDIRECT, OR CONSEQUENTIAL DAMAGES, OR
              ANY LOST REVENUE, LOST PROFITS, LOSS OF TECHNOLOGY, LOSS OF DATA, REPLACEMENT GOODS OR
              SERVICES, OR INTERRUPTION OF BUSINESS.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>10.2. Limitation of Liability Amount.</strong> TO THE FULLEST EXTENT PERMITTED
              BY APPLICABLE LAW, EACH PARTY’S MAXIMUM AGGREGATE LIABILITY ARISING FROM OR RELATED TO
              THIS AGREEMENT OR THE SERVICES WILL BE LIMITED TO THE TOTAL AMOUNT PAID OR PAYABLE BY
              CUSTOMER TO MERUDESK UNDER THIS AGREEMENT FOR THE TWELVE (12) MONTH PERIOD IMMEDIATELY
              PRIOR TO THE DATE OF THE INCIDENT GIVING RISE TO THE LIABILITY, EXCEPT WITH RESPECT TO
              A PARTY’S INDEMNIFICATION OBLIGATIONS OR A PARTY’S FRAUD, GROSS NEGLIGENCE, OR WILLFUL
              MISCONDUCT.
              <br />
              <br />
              THE LIMITATIONS SET FORTH IN THIS SECTION WILL APPY TO ANY THEORY OF LIABILITY,
              WHETHER IN CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR OTHERWISE AND
              EVEN IF THE PARTY IS ADVISED OF THE POSSIBILITY OF SUCH DAMAGES AND/OR ANY LIMITED
              REMEDEDY UNDER THIS AGREEMENT FAILS ITS ESSENTIAL PURPOSE.
            </Typography>
          </Box>

          {/* Section 11 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              11. Suspension of Services
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              MeruDesk reserves the right to, immediately and without liability, suspend provision
              of the Services: (a) if any undisputed (in good faith) Fees or other amounts payable
              to MeruDesk under this Agreement remain unpaid for more than 5 days following written
              notice to Customer thereof, until those Fees are paid in full; (b) permanently with
              respect to any individual User or Customer Data that violates the “Acceptable Use
              Policy” section of this Agreement; and/or (c) as MeruDesk deems necessary, at its sole
              discretion, to address an actual or reasonably suspected threat to the security,
              integrity, or availability of the Services, in which case MeruDesk will use
              commercially reasonable efforts to provide Customer with prior notice of the
              suspension and limit the suspension to the affected portion of the Services, and
              MeruDesk will resume provision of the Services promptly if and when the cause of the
              suspension is resolved to MeruDesk’s sole satisfaction or this Agreement is otherwise
              terminated in accordance with its terms.
            </Typography>
          </Box>

          {/* Section 12 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              12. Termination
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>12.1. Termination for Cause.</strong>Either party may terminate this Agreement or the applicable Service Order if the other party materially breaches this Agreement and fails to remedy that breach within 30 days after receiving written notice reasonably detailing the breach. Notwithstanding anything to the contrary in this Agreement, if Customer terminates this Agreement and/or a Service Order as a result of MeruDesk’s material breach, then MeruDesk will refund, within 30 days of receiving written notice of termination from customer, to Customer the pro-rata amount of any pre-paid Fees for the portion of the applicable Services remaining prospectively unused as a result of the termination.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>12.2. Termination for Insolvency.</strong> Either party may terminate this
              Agreement upon written notice to the other party if the other party ceases business
              operations without a successor or seeks protection under, or has instituted against
              it, any bankruptcy, receivership, trust deed, creditors’ arrangement, composition, or
              comparable proceeding that is not dismissed within 60 days.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>12.3. Effect of Termination.</strong> Upon the expiration or termination of
              this Agreement or a Service Order: (a) Customer’s right to use the terminated Services
              will cease; (b) Customer will return to MeruDesk any Confidential Information in
              Customer’s possession and will delete all copies of Documentation in Customer’s
              possession; and (c) for a period of 30 days immediately following expiration or
              termination, MeruDesk will provide Customer the limited right to access applicable
              functionality of the Services solely for the purpose of extracting Customer Data from
              the Services in a manner reasonably offered by MeruDesk, after which time Customer’s
              access to Customer Data will be discontinued.
            </Typography>
          </Box>

          {/* Section 13 */}
          <Box>
            <Typography variant="h6" color="text.primary" mb={1.5}>
              13. Miscellaneous
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.1. Legal Compliance.</strong> MeruDesk and Customer will each comply with
              all laws applicable to the respective party in connection with its performance under
              this Agreement.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.2. Assignment.</strong> Neither party may assign this Agreement or its
              obligations under this Agreement, in whole or in part, by operation of law or
              otherwise, without the prior written consent of the other party, except in connection
              with a corporate reorganization, consolidation, merger, acquisition, or sale of all or
              substantially all of the assigning party’s assets and upon notice to the other party
              in each case. An attempted assignment in violation of the foregoing restrictions will
              be void. Subject to the restrictions of this Section, this Agreement will be binding
              on, and will inure to the benefit of, the parties and their respective successors and
              permitted assigns.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.3. Notices.</strong> All notices required by this Agreement will be in
              writing and delivered by personal delivery, certified mail with return receipt
              requested, overnight courier, Email, or by any other method agreed upon by the parties
              in writing and will be delivered to: (a) if to Customer, to the administrator contact
              that Customer designates in Customer’s account within the Services; (b) if to
              MeruDesk, to CyberMeru Technologies Pvt. Ltd., A3, Srushti, Near Guruganesh Nagar, DP
              Road, Kothrud, Pune - 411038, Maharashtra, India. or to legal@MeruDesk.com; or (c) to
              such other contact as may be designated by this Agreement for a specific type of
              notice.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.4. Independent Contractors.</strong> MeruDesk and Customer are independent
              contractors of each other. Nothing in this Agreement will be deemed to create an
              agency, employment, partnership, fiduciary, or joint venture between the parties nor
              the right of a party to make any representations or incur any obligations on behalf of
              the other party.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.5. Severability.</strong> If any provision of this Agreement is determined
              by an adjudicator of competent jurisdiction to be illegal, invalid, or unenforceable,
              that provision will be enforced to the maximum extent permissible so as to effect the
              intent of the parties, and all other provisions of this Agreement will be unaffected
              and will remain in effect.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.6. No Waiver.</strong> A party’s failure to exercise or its delay in
              exercising any right or remedy under this Agreement will not operate as a waiver
              thereof; nor will a party’s waiver of any right or remedy under this Agreement operate
              as a subsequent waiver of the same or any other right or remedy.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.7. Governing Law; Jurisdiction.</strong> This Agreement will be governed by
              the laws of India without regard to conflict of laws provisions, and any dispute
              arising from or relating to this Agreement will be adjudicated exclusively in the
              courts having jurisdiction over the matter located in Pune, State of Maharashtra,
              India.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.8. Force Majeure.</strong> Neither Party will be liable under or in breach
              of this Agreement for any partial or total failure or delay in the performing its
              obligations under this Agreement on account of riots, fire, flood, earthquake,
              explosion, epidemics, war, strike or labor disputes (not involving the subject party),
              embargo, civil or military authority, act of God, governmental action, or other causes
              beyond its reasonable control and without the fault or negligence of the
              non-performing party (a “Force Majeure Event”). In the event of a Force Majeure Event,
              the non-performing party will resume performance as soon as practicable once the Force
              Majeure Event is eliminated. If a Force Majeure Event continues for more than 30 days,
              then the non-delaying party may terminate the affected Service Order and, if Customer
              is the terminating party, MeruDesk will refund to Customer the pro-rata amount of any
              pre-paid Fees for the portion of the applicable Services remaining prospectively
              unused as a result of the termination.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.9. Including.</strong> The word “including” as used in this Agreement means
              “including but not limited to,” unless otherwise stated in the Agreement.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.10. Document Precedence.</strong> In the event of a conflict between the
              terms of a Service Order and the terms of this Master Services Agreement, the terms of
              the Service Order will control. The terms of this Agreement control over any
              additional or inconsistent terms or conditions contained in any corresponding purchase
              order or other purchasing or invoicing documentation provided by Customer, all of
              which are rejected and will be of no effect even if they state they supersede prior or
              contemporaneous agreements with respect to the subject matter thereof.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.11. Survival.</strong> Any terms of this Agreement that expressly or by
              their nature are to continue in effect after the expiration or termination of this
              Agreement will survive expiration or termination and will continue in their effect.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.12. Electronic Signature; Counterparts.</strong> Original signatures by
              electronic signature methods or by electronic transmission of a signed document are
              true and valid signatures for all purposes and will bind the parties to the same
              extent as that of handwritten signatures. ​This Agreement may be executed in
              counterparts, each of which is deemed an original and all of which together are deemed
              to be one and the same agreement.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.13. Entire Agreement; Modification.</strong> All documents of any nature
              that incorporate or are incorporated into this Agreement will be read and interpreted
              together with, and made part of, this Agreement as a single document. This Agreement
              sets forth the complete and final agreement between the parties with respect to the
              subject matter hereof and supersedes all prior or contemporaneous agreements,
              understandings, negotiations, or discussions between the parties, whether oral or
              written, related to the subject matter of this Agreement. This Agreement may only be
              modified, amended, or any rights under it waived, by a written document executed by
              the party or parties to be bound.
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>13.14. Regulatory Compliance & Review.</strong> We regularly review our
              policies and practices to ensure compliance with all applicable legal, statutory,
              regulatory, and contractual requirements, including the Digital Personal Data
              Protection Act, 2023 (India), EU GDPR (where applicable), and global standards related
              to information security and data protection. This includes periodic legal assessments
              and updates aligned with ISO/IEC 27001:2022 requirements.
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Container>
  </Box>
);

export default TermsOfService;
